import { useRouter } from 'next/router';
import { getMessageWithPlaceholders } from 'shared-locales';
import { useReduxState } from 'hooks';
import { useApiData } from 'lib/api';
import {
  ApiDownloadButton,
  EmptyIndicator,
  FlexContainer,
  LoadingIndicator,
  Pagination
} from 'components/common';
import { Box } from 'components/common/box';
import { CompletionHistoryTable } from 'components/common/completions';
import { DropdownSelectable } from 'components/common/dropdown';
import { AnalyticsCompletionHistoryStats } from '.';

import type { GetCompletionsRes } from 'shared-types/responses';

const AnalyticsCompletionHistoryTable = () => {
  const { messages } = useReduxState();

  const router = useRouter();

  const completionsPerPage =
    parseInt(router.query?.completionsPerPage as string, 10) || 10;

  const apiUrl = `/admin/analytics/completions`;

  const queryParams = Object.keys(router?.query || {})
    .map((key) => `&${key}=${router.query[key]}`)
    .join('');

  const { data, loading } = useApiData<GetCompletionsRes>({
    url: `${apiUrl}?limit=${completionsPerPage}${queryParams}`
  });

  const handleCompletionsPerPageChange = async (value: number) => {
    const query = { ...router.query, page: 1, completionsPerPage: value };
    router.replace({ query }, { query }, { shallow: true });
  };

  const handlePageChange = ({ page }: { page: number }) => {
    router.replace({ query: { ...router.query, page } }, undefined, {
      shallow: true
    });
  };

  return (
    <div className="completions">
      <AnalyticsCompletionHistoryStats
        numberOfCompletions={data?.numberOfCompletions}
        numberOfCompletionsByType={data?.numberOfCompletionsByType}
      />

      <Box
        headerContent={
          <FlexContainer style={{ flexWrap: 'wrap', gap: 10 }}>
            <DropdownSelectable
              category="lighter"
              defaultText={`${messages.commonSelect}...`}
              defaultValue={completionsPerPage}
              dropdownTextMinWidth="120px"
              onChange={handleCompletionsPerPageChange}
              options={[
                {
                  text: getMessageWithPlaceholders({
                    message: messages.achievementNumberOfAchievementCompletions,
                    placeholders: { numberOfCompletions: '10' }
                  }),
                  value: 10
                },
                {
                  text: getMessageWithPlaceholders({
                    message: messages.achievementNumberOfAchievementCompletions,
                    placeholders: { numberOfCompletions: '25' }
                  }),
                  value: 25
                },
                {
                  text: getMessageWithPlaceholders({
                    message: messages.achievementNumberOfAchievementCompletions,
                    placeholders: { numberOfCompletions: '50' }
                  }),
                  value: 50
                }
              ]}
              selectedValueBefore={`${messages.commonShow}:`}
              showSelectedText={true}
              size="header"
              titleMaxWidth={120}
              value={completionsPerPage}
            />

            <ApiDownloadButton
              category="success"
              filename={`${messages.profileCompletionHistory}.xlsx`}
              icon="file-excel"
              size="header"
              style={{ whiteSpace: 'nowrap' }}
              text={messages.usersDownloadSummary}
              url={`${apiUrl}?exportToExcel=true${queryParams}`}
            />
          </FlexContainer>
        }
        headerContentWrap={true}
        padding={0}
        title={messages.profileCompletionHistory}
      >
        {loading ? (
          <LoadingIndicator />
        ) : data?.completions?.length ? (
          <CompletionHistoryTable completions={data.completions} />
        ) : (
          <EmptyIndicator icon="list" title={messages.commonNoData} />
        )}
      </Box>

      <Pagination
        currentPage={Number(router.query?.page) || 1}
        numberOfPages={data?.pages || 1}
        onPageChange={handlePageChange}
      />
    </div>
  );
};

export default AnalyticsCompletionHistoryTable;
